import React, { useState } from "react";
import styled from "styled-components";

import Avatar from "../avatar/Avatar";
import Badge from "../badge/Badge";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../date-display/DateDisplay";
import type { DmThread, DmThreadStatus } from "../../app/services/dmThreads";
import StatusBadge from "../status-badge/StatusBadge";
import { isClientGroupPosition } from "../../utils/memberPositionUtils";

interface StatusPresentation {
  requester: string;
  contractor: string;
  requesterHighlight: boolean;
  contractorHighlight: boolean;
}

// ステータスに対応する表示テキストと強調（青枠）ルール
//
// 注: subcontract のドラフト（``draft`` / ``new``）は `DraftThreadItem` 側で
// 別レンダリングされるため、ここでは実質的に到達しない。型の網羅性のため
// ``draft`` も ``new`` と同じ表示で定義しておく。
const STATUS_PRESENTATION: Record<DmThreadStatus, StatusPresentation> = {
  draft: {
    requester: "承認待ち",
    contractor: "NEW",
    requesterHighlight: false,
    contractorHighlight: true,
  },
  new: {
    requester: "承認待ち",
    contractor: "NEW",
    requesterHighlight: false,
    contractorHighlight: true,
  },
  estimating: {
    requester: "お見積待ち",
    contractor: "お見積作成中",
    requesterHighlight: false,
    contractorHighlight: true,
  },
  estimate_review: {
    requester: "NEW",
    contractor: "お見積審査中",
    requesterHighlight: true,
    contractorHighlight: false,
  },
  contractor_pending: {
    requester: "オファー承認待ち",
    contractor: "オファーを確認",
    requesterHighlight: false,
    contractorHighlight: true,
  },
  rejected: {
    requester: "不成立",
    contractor: "辞退",
    requesterHighlight: false,
    contractorHighlight: false,
  },
  in_progress: {
    requester: "進行中",
    contractor: "進行中",
    requesterHighlight: false,
    contractorHighlight: true,
  },
  inspection_pending: {
    requester: "検収をお願いします",
    contractor: "検収待ち",
    requesterHighlight: true,
    contractorHighlight: false,
  },
  closed: {
    requester: "クローズ",
    contractor: "クローズ",
    requesterHighlight: false,
    contractorHighlight: false,
  },
  completed: {
    requester: "お疲れさまでした",
    contractor: "お疲れさまでした",
    requesterHighlight: false,
    contractorHighlight: false,
  },
};

// 左ボーダーの色
const BORDER_COLOR: Record<DmThreadStatus, string> = {
  draft: "var(--soremo-blue)",
  new: "var(--soremo-blue)",
  estimating: "var(--soremo-border)",
  estimate_review: "var(--soremo-border)",
  contractor_pending: "var(--soremo-border)",
  rejected: "var(--soremo-gray)",
  in_progress: "var(--soremo-blue)",
  inspection_pending: "var(--soremo-blue)",
  closed: "var(--soremo-gray)",
  completed: "var(--soremo-gray)",
};

// メンバーシップ情報の型（avatar stack用）
export interface MembershipForAvatar {
  user_id?: number;
  display_name_jp?: string;
  avatar_url?: string;
  position?: string;
}

interface ThreadItemProps {
  thread: DmThread;
  currentUserId?: number;
  isMasterClient?: boolean;
  isSelected?: boolean;
  onClick: () => void;
  unreadCount?: number;
  memberships?: MembershipForAvatar[];
  /**
   * 任意の金額ラベル（例: `¥11,000` / `-¥16,500`）。指定時のみ表示名の右側に
   * 表示する。振込申請リストで対価（プラス）/ 支出（マイナス）を見せるために使う。
   * マイナス（先頭が `-`）は控えめなグレーで表示し、控除であることを示す。
   */
  amountLabel?: string | null;
  /**
   * 旧環境（`Product.is_new_payment_model=false`）取引のとき `true` を指定すると、
   * 表示名の隣に「旧環境」バッジを表示する。新 SPA の取引対価（`balance_settled` /
   * PaymentLedger）と紐づかないことを判別するための表示で、進行中モーダル
   * （`TransactionsModal`）でのみ使う。DM 一覧では指定しないため非表示のまま。
   */
  isLegacyEnvironment?: boolean;
}

const ThreadItemContainer = styled.div<{
  $isSelected: boolean;
  $isHover: boolean;
  $isActive: boolean;
  $borderColor: string;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px 4px 8px 8px;
  cursor: ${({ $isSelected }) => ($isSelected ? "default" : "pointer")};
  user-select: none;
  background-color: ${({ $isSelected, $isActive, $isHover }) =>
    $isSelected
      ? "var(--soremo-border)"
      : $isActive
        ? "var(--soremo-border)"
        : $isHover
          ? "var(--soremo-background)"
          : "#ffffff"};
  transition: background-color 0.2s ease;
  border-bottom: 1px solid var(--soremo-border);
  border-left: 6px solid ${({ $borderColor }) => $borderColor};
`;

const ReceiverInfo = styled.div`
  width: 100%;
  font-size: 11px;
  line-height: 100%;
  color: var(--soremo-light-gray);
`;

const Icon = styled.span<{
  $isHover: boolean;
  $isSelected: boolean;
}>`
  font-size: 24px;
  color: ${({ $isSelected, $isHover }) =>
    $isSelected || $isHover
      ? "var(--soremo-light-gray)"
      : "var(--soremo-border)"};
`;

// 本契約スレッドのアバタースタック表示設定
const MAX_VISIBLE_AVATARS = 4;
const STACK_AVATAR_SIZE = 20;
const STACK_OVERLAP_MARGIN = "-6px";
const STACK_GROUP_BOUNDARY_MARGIN = "4px";

// 注: グローバルコンポーネント `src/components/avatar-stack/AvatarStack.tsx` と
// 名前衝突しないよう、契約スレッド固有のスタック表示用 styled 要素は
// `MasterContractAvatarRow` として命名する。
const MasterContractAvatarRow = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const StackedAvatarWrapper = styled.div<{
  $index: number;
  $total: number;
  $isGroupBoundary: boolean;
}>`
  position: relative;
  margin-left: ${({ $isGroupBoundary }) =>
    $isGroupBoundary ? STACK_GROUP_BOUNDARY_MARGIN : STACK_OVERLAP_MARGIN};
  z-index: ${({ $total, $index }) => $total - $index};
  &:first-child {
    margin-left: 0;
  }
`;

const OverflowCounter = styled.span.attrs({ className: "label8" })`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: -4px;
  background: #f0f0f0;
  border: 1px solid #fff;
  border-radius: 50%;
  font-size: 8px;
`;

const MasterContractIcon = styled.span`
  font-size: 20px;
  color: var(--soremo-blue);
`;

// 表示名の右側に積む「金額 + 日付」の縦並び。金額ラベル指定時のみ金額を出す。
const NameRowRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
`;

// 金額ラベル。マイナス（控除/支出）は控えめなグレーで表示する。
const AmountLabel = styled.span<{ $negative: boolean }>`
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $negative }) =>
    $negative ? "var(--soremo-light-gray)" : "var(--soremo-text)"};
`;

const ThreadItem: React.FC<ThreadItemProps> = ({
  thread,
  currentUserId,
  isMasterClient = false,
  isSelected = false,
  onClick,
  unreadCount = 0,
  memberships = [],
  amountLabel = null,
  isLegacyEnvironment = false,
}) => {
  const [isHover, setIsHover] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const isMasterContract = thread.thread_type === "master_contract";

  // subcontract の場合、ログインユーザーが requester か contractor かを判定
  const isRequester = isMasterContract
    ? isMasterClient
    : thread.requester?.id === currentUserId;

  const statusPresentation = STATUS_PRESENTATION[thread.status] ?? {
    requester: thread.status,
    contractor: thread.status,
    requesterHighlight: false,
    contractorHighlight: false,
  };

  const statusText = isRequester
    ? statusPresentation.requester
    : statusPresentation.contractor;

  const isStatusHighlighted = isRequester
    ? statusPresentation.requesterHighlight
    : statusPresentation.contractorHighlight;
  const borderColor = BORDER_COLOR[thread.status] ?? "var(--soremo-border)";

  // 納期までの残り日数を表示
  const getDeadlineText = (): string | null => {
    if (!thread.deadline) return null;
    const now = new Date();
    const deadline = new Date(thread.deadline);
    const diffMs = deadline.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays > 0) return `納期まであと${diffDays}日`;
    if (diffDays === 0) return "納期は本日";
    return `納期${Math.abs(diffDays)}日超過`;
  };

  const deadlineText = getDeadlineText();

  // 表示名の決定
  const getDisplayName = (): string => {
    if (isMasterContract) {
      return thread.assigned_roles || "このプロジェクトの契約";
    }
    if (isRequester) {
      return thread.contractor?.display_name ?? "受託者";
    }
    return "あなた";
  };

  const renderAvatars = () => {
    if (isMasterContract) {
      return (
        <MasterContractIcon className="material-symbols-rounded">
          workspace_premium
        </MasterContractIcon>
      );
    }

    return (
      <Avatar
        size={48}
        src={thread.contractor?.avatar_url}
        name={thread.contractor?.display_name}
      />
    );
  };

  // 本契約: アバタースタック描画
  // memberships は CLIENT グループ → PRODUCER グループの順で渡される前提。
  // グループ境界の直後のアバターのみ左余白を通常より広げ、グループ間のやりとりを視覚化する。
  const renderMasterAvatarStack = () => {
    const visibleMembers = memberships.slice(0, MAX_VISIBLE_AVATARS);
    const remaining = memberships.length - visibleMembers.length;

    if (visibleMembers.length === 0) return null;

    return (
      <MasterContractAvatarRow>
        {visibleMembers.map((m, i) => {
          const prev = visibleMembers[i - 1];
          const isGroupBoundary =
            i > 0 &&
            isClientGroupPosition(prev?.position ?? "") &&
            !isClientGroupPosition(m.position ?? "");
          return (
            <StackedAvatarWrapper
              key={m.user_id ?? i}
              $index={i}
              $total={visibleMembers.length}
              $isGroupBoundary={isGroupBoundary}
            >
              <Avatar
                size={STACK_AVATAR_SIZE}
                src={m.avatar_url}
                name={m.display_name_jp}
              />
            </StackedAvatarWrapper>
          );
        })}
        {remaining > 0 && <OverflowCounter>+{remaining}</OverflowCounter>}
      </MasterContractAvatarRow>
    );
  };

  return (
    <ThreadItemContainer
      $isSelected={isSelected}
      $isHover={isHover}
      $isActive={isActive}
      $borderColor={borderColor}
      onClick={onClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => {
        setIsHover(false);
        setIsActive(false);
      }}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
    >
      {/* 受信した subcontract の場合、送信者の情報を上部に表示 */}
      {!isMasterContract && !isRequester && thread.requester && (
        <div className="u-w100 u-row u-gap8 u-pl24">
          <Avatar
            size={24}
            src={thread.requester.avatar_url}
            name={thread.requester.display_name}
          />
          <ReceiverInfo>
            {thread.requester.display_name}さんからのオファー
          </ReceiverInfo>
        </div>
      )}

      <div className="u-w100 u-row u-gap8">
        {renderAvatars()}

        <div className="u-w100 u-col u-gap8">
          <div className="u-w100 u-row-between">
            <div className="u-row u-gap8">
              <div className="heading-spacing-13">
                {getDisplayName()}
              </div>
              {isLegacyEnvironment && (
                <StatusBadge
                  label="旧環境"
                  size="small"
                  color="border"
                />
              )}
            </div>
            <NameRowRight>
              {amountLabel && (
                <AmountLabel
                  className="bodytext-13"
                  $negative={amountLabel.trim().startsWith("-")}
                >
                  {amountLabel}
                </AmountLabel>
              )}
              <div className="label8 u-text-light-gray">
                <DateDisplay
                  date={thread.updated_at}
                  type={DATE_DISPLAY_TYPE.ADAPTIVE}
                />
              </div>
            </NameRowRight>
          </div>

          {isMasterContract && renderMasterAvatarStack()}

          <div className="u-w100 u-row-between">
            <div className="bodytext-11 u-line-height-100">
              {thread.assigned_roles
                ? thread.scope
                  ? `${thread.assigned_roles}（${thread.scope}）`
                  : thread.assigned_roles
                : thread.scope || "—"}
            </div>
            <StatusBadge
              variant="inline"
              label={
                thread.status === "in_progress" && deadlineText
                  ? deadlineText
                  : statusText
              }
              highlighted={isStatusHighlighted}
            />
          </div>
        </div>

        <div className="u-row">
          {unreadCount > 0 && <Badge count={unreadCount} />}
          <Icon
            className="material-symbols-rounded"
            $isHover={isHover}
            $isSelected={isSelected}
          >
            chevron_right
          </Icon>
        </div>
      </div>
    </ThreadItemContainer>
  );
};

export default ThreadItem;
